import api from "../../utils/api";

const url = process.env.VUE_APP_API_BASE_URL || '';
export default {
  namespaced: false,
  state: {
    user: null,
    authChecking: false,
    mustChangePassword: false,
  },
  mutations: {
    setUser(state, payload) {
      state.user = payload;
    },
    setAuthChecking(state, checking) {
      state.authChecking = checking;
    },
    setMustChangePassword(state, mustChange) {
      state.mustChangePassword = mustChange;
    },
  },
  getters: {
    userCompany: (state) => {
      return state.user?.company || null;
    },
    userRole: (state) => {
      return state.user?.role || 'user';
    },
    isSuperAdmin: (state) => {
      return state.user?.role === 'superadmin';
    },
    isOwner: (state) => {
      return state.user?.role === 'owner';
    },
    isAdmin: (state) => {
      return ['admin', 'owner', 'superadmin'].includes(state.user?.role);
    },
    canManageUsers: (state) => {
      return ['admin', 'owner', 'superadmin'].includes(state.user?.role);
    },
    canSeeAllData: (state) => {
      return state.user?.role === 'superadmin' || state.user?.role === 'owner';
    }
  },
  actions: {
    async loginUser({ commit }, userData) {
      const response = await api.post(`${url}/apinvoices/users/login`, userData);
      if (response.ok) {
        commit("setUser", response.user);
        commit("setMustChangePassword", response.mustChangePassword || false);
      }
      return response;
    },
    async checkAuth({ commit }) {
      commit("setAuthChecking", true);
      try {
        const response = await api.get(`${url}/apinvoices/users/check-auth`);
        if (response.ok) {
          commit("setUser", response.user);
        } else {
          commit("setUser", null);
        }
        return response;
      } catch (error) {
        commit("setUser", null);
        return { ok: false, error: error.message };
      } finally {
        commit("setAuthChecking", false);
      }
    },
    async logOut({ commit }) {
      try {
        await api.post(`${url}/apinvoices/users/logout`);
      } finally {
        commit("setUser", null);
        commit("setAuthChecking", false);
      }
    },
    async forgotPassword(_, data) {
      return await api.post(`${url}/apinvoices/users/forgot-password`, data);
    },
    async verifyOtp(_, data) {
      return await api.post(`${url}/apinvoices/users/verify-otp`, data);
    },
    async setNewPassword(_, data) {
      return await api.post(`${url}/apinvoices/users/set-new-password`, data);
    },
    async getApprovedWorkOrders(_, filters = {}) {
      try {
        const queryParams = new URLSearchParams(filters).toString();
        const response = await api.get(`${url}/apinvoices/users/approved-work-orders?${queryParams}`);
        return response;
      } catch (error) {
        console.error("Error fetching approved work orders:", error);
        return { ok: false, error: "Failed to fetch approved work orders" };
      }
    },
    async getSubmittedInvoices(_, filters = {}) {
      try {
        const queryParams = new URLSearchParams(filters).toString();
        const response = await api.get(`${url}/apinvoices/users/submitted-invoices?${queryParams}`);
        return response;
      } catch (error) {
        console.error("Error fetching submitted invoices:", error);
        return { ok: false, error: "Failed to fetch submitted invoices" };
      }
    },
    async getPaidInvoices(_, filters = {}) {
      try {
        const queryParams = new URLSearchParams(filters).toString();
        const response = await api.get(`${url}/apinvoices/users/paid-invoices?${queryParams}`);
        return response;
      } catch (error) {
        console.error("Error fetching paid invoices:", error);
        return { ok: false, error: "Failed to fetch paid invoices" };
      }
    },
    async getDeclinedInvoices(_, filters = {}) {
      try {
        const queryParams = new URLSearchParams(filters).toString();
        const response = await api.get(`${url}/apinvoices/users/declined-invoices?${queryParams}`);
        return response;
      } catch (error) {
        console.error("Error fetching declined invoices:", error);
        return { ok: false, error: "Failed to fetch declined invoices" };
      }
    },
    async getReturnInvoices(_, filters = {}) {
      try {
        const queryParams = new URLSearchParams(filters).toString();
        const response = await api.get(`${url}/apinvoices/users/return-invoices?${queryParams}`);
        return response;
      } catch (error) {
        console.error("Error fetching return invoices:", error);
        return { ok: false, error: "Failed to fetch return invoices" };
      }
    },
    async getOwnerDashboard(_, filters = {}) {
      try {
        const queryParams = new URLSearchParams(filters).toString();
        const response = await api.get(`${url}/apinvoices/users/owner/dashboard?${queryParams}`);
        return response;
      } catch (error) {
        console.error("Error fetching owner dashboard:", error);
        return { ok: false, error: "Failed to fetch owner dashboard" };
      }
    },
    async getRowAttachments(_, { rowId, sheetId }) {
      try {
        const response = await api.get(`${url}/apinvoices/users/attachments/${rowId}/${sheetId}`);
        return response;
      } catch (error) {
        console.error("Error fetching attachments:", error);
        return { ok: false, error: "Failed to fetch attachments" };
      }
    },
    async uploadRowAttachment(_, { rowId, sheetId, file }) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${url}/apinvoices/users/attachments/${rowId}/${sheetId}`, {
          method: 'POST',
          body: formData,
          credentials: 'include'
        });

        const data = await response.json();
        
        if (response.ok) {
          return { ok: true, data: data.data };
        } else {
          return { ok: false, error: data.error || "Failed to upload attachment" };
        }
      } catch (error) {
        console.error("Error uploading attachment:", error);
        return { ok: false, error: "Failed to upload attachment" };
      }
    },
    async getAllCategories() {
      try {
        const response = await fetch(`${url}/apinvoices/users/categories`, {
          credentials: 'include'
        });
        const data = await response.json();
        
        if (response.ok) {
          return { ok: true, data: data.data };
        } else {
          return { ok: false, error: data.error || "Failed to get categories" };
        }
      } catch (error) {
        console.error("Error getting categories:", error);
        return { ok: false, error: "Failed to get categories" };
      }
    },
    async getAllStatuses() {
      try {
        const response = await fetch(`${url}/apinvoices/users/statuses`, {
          credentials: 'include'
        });
        const data = await response.json();
        
        if (response.ok) {
          return { ok: true, data: data.data };
        } else {
          return { ok: false, error: data.error || "Failed to get statuses" };
        }
      } catch (error) {
        console.error("Error getting statuses:", error);
        return { ok: false, error: "Failed to get statuses" };
      }
    },
    async getWorkOrderStatuses() {
      try {
        const response = await fetch(`${url}/apinvoices/users/wo-statuses`, {
          credentials: 'include'
        });
        const data = await response.json();
        
        if (response.ok) {
          return { ok: true, data: data.data };
        } else {
          return { ok: false, error: data.error || "Failed to get work order statuses" };
        }
      } catch (error) {
        console.error("Error getting work order statuses:", error);
        return { ok: false, error: "Failed to get work order statuses" };
      }
    },
    async getCompanies() {
      try {
        const response = await api.get(`${url}/apinvoices/users/companies`);
        return response;
      } catch (error) {
        console.error("Error fetching companies:", error);
        return { ok: false, error: "Failed to fetch companies" };
      }
    },
    async getUsers(_, filters = {}) {
      try {
        const queryParams = new URLSearchParams(filters).toString();
        
        const response = await api.get(`${url}/apinvoices/users/admin/users?${queryParams}`);
        
        return response;
      } catch (error) {
        console.error("Error fetching users:", error);
        return { ok: false, error: "Failed to fetch users" };
      }
    },
    async createUser(_, userData) {
      try {
        const response = await api.post(`${url}/apinvoices/users/admin/users`, userData);
        return response;
      } catch (error) {
        console.error("Error creating user:", error);
        return { ok: false, error: "Failed to create user" };
      }
    },
    async updateUser(_, { id, userData }) {
      try {
        const response = await api.put(`${url}/apinvoices/users/admin/users/${id}`, userData);
        return response;
      } catch (error) {
        console.error("Error updating user:", error);
        return { ok: false, error: "Failed to update user" };
      }
    },
    async resendCredentials(_, userId) {
      try {
        if (!userId) {
          return { ok: false, error: "Invalid user ID" };
        }
        const response = await api.post(`${url}/apinvoices/users/admin/users/${userId}/resend-credentials`);
        return response;
      } catch (error) {
        console.error("Error resending credentials:", error);
        return { ok: false, error: "Failed to resend credentials" };
      }
    },
    async toggleUserActive(_, userId) {
      try {
        if (!userId) {
          return { ok: false, error: "Invalid user ID" };
        }
        const response = await api.patch(`${url}/apinvoices/users/admin/users/${userId}/toggle-active`);
        return response;
      } catch (error) {
        console.error("Error toggling user status:", error);
        return { ok: false, error: "Failed to toggle user status" };
      }
    },
    async deleteUser(_, userId) {
      try {
        if (!userId) {
          return { ok: false, error: "Invalid user ID" };
        }
        const response = await api.delete(`${url}/apinvoices/users/admin/users/${userId}`);
        return response;
      } catch (error) {
        console.error("Error deleting user:", error);
        return { ok: false, error: "Failed to delete user" };
      }
    },
  },
};


